import {
  CHARGE_MAYBE_TAKEN_CONFLICT,
  DB_DEADLOCK_CONFLICT,
  EARLIER_ORDER_PAID_CONFLICT,
  PALLET_PRICE_CHANGED_CONFLICT,
  REFERRAL_CODE_REFUSAL,
  SCAN_GO_CLAIM_LOST_CONFLICT,
} from '@stopal/shared/errors'
import { TRPCClientError } from '@trpc/client'

/**
 * How the web app classifies a failed tRPC call.
 *
 * Both predicates read `TRPCClientError.data`, which is populated from the
 * server's error shape — so it is present exactly when a structured tRPC error
 * came back, and absent otherwise. That is a property of `@trpc/client`, not of
 * this repo, and it is the only thing either predicate knows.
 *
 * It lives here rather than in `trpc/query-client.ts` because four component
 * modules import it to decide what to render; that file's job is building the
 * `QueryClient`, and it consumes `isUnauthorized` from here like everyone else.
 */

/**
 * An UNAUTHORIZED response is deterministic — the session is absent or dead,
 * and retrying cannot change that. Retrying it anyway (the default exponential
 * backoff) holds every auth-gated surface on its loading state for ~8s before
 * the signed-out redirect can fire.
 */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data?.code === 'UNAUTHORIZED'
}

/**
 * A `CONFLICT` that is the DATABASE's, not this surface's.
 *
 * `CONFLICT` carries every domain refusal the server writes AND the envelope a
 * Postgres deadlock (`40P01`) is mapped to, so a branch reading the code alone
 * absorbs both — and answers a lost lock race with whatever sentence that
 * surface's own conflict deserves. The server names its kind
 * (`DB_DEADLOCK_CONFLICT`); a surface asking "is this MY conflict" excludes it.
 *
 * The generic copy is still shown — a deadlock's message is authored, says to
 * try again, and `CONFLICT` is in `AUTHORED_CODES` — so excluding it here means
 * "fall through to the generic error", never "say nothing".
 */
export function isDatabaseConflict(error: unknown): boolean {
  // STRUCTURAL, where its neighbours above test `instanceof TRPCClientError`.
  // Not an oversight: the native auth bridge inspects a caught `unknown` and
  // an `instanceof` gate would have excluded it, so it hand-rolled this same
  // test — two spellings of one predicate, which is what the shared constant
  // was supposed to prevent. Reading `data.conflictKind` needs no class: the
  // field exists exactly when the server put it there.
  if (typeof error !== 'object' || error === null || !('data' in error))
    return false
  const data = (error as { data?: { conflictKind?: string } }).data
  return data?.conflictKind === DB_DEADLOCK_CONFLICT
}

/**
 * A charge under this attempt may already have gone through — the customer is
 * shown their orders, not a failure. An earlier version of the order having history-ok: an order version, not code history
 * been paid is the same answer with a surer sentence (`isEarlierOrderPaid`).
 */
export function isChargeMaybeTaken(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false
  const kind = error.data?.conflictKind
  return (
    kind === CHARGE_MAYBE_TAKEN_CONFLICT || kind === EARLIER_ORDER_PAID_CONFLICT
  )
}

/**
 * A Scan & Go claim was lost and the MONEY QUESTION IS SETTLED — nothing was
 * taken, or what was taken has been refunded in full.
 *
 * The sheet must not offer "Pay as a new order" here, and must not keep the
 * attempt marked uncertain: both of those exist for a payment that may have
 * gone through, and this one demonstrably has not or has been reversed. What
 * the customer does next is put the pallet back and rescan, which starts a
 * fresh attempt of its own.
 *
 * Deliberately NOT folded into `isChargeMaybeTaken`: the surfaces that pay
 * typically answer that kind with an uncertain notice of their own INSTEAD of
 * the server's sentence, and the sentence is the whole point here — it names
 * which pallet went and says the refund happened.
 */
export function isScanGoClaimLost(error: unknown): boolean {
  return (
    error instanceof TRPCClientError &&
    error.data?.conflictKind === SCAN_GO_CLAIM_LOST_CONFLICT
  )
}

/** The buy sheet's price is stale: nothing was charged, and Pay would be refused again. */
export function isPalletPriceChanged(error: unknown): boolean {
  return (
    error instanceof TRPCClientError &&
    error.data?.conflictKind === PALLET_PRICE_CHANGED_CONFLICT
  )
}

/** The server knows an earlier version of this order was paid, and wrote it. history-ok: an order version, not code history */
export function isEarlierOrderPaid(error: unknown): boolean {
  return (
    error instanceof TRPCClientError &&
    error.data?.conflictKind === EARLIER_ORDER_PAID_CONFLICT
  )
}

/**
 * The server refused a charge with no charge in existence, so the next press may
 * be a new charge attempt — and must be, or a decline would be replayed.
 *
 * Read it only together with the attempt having NOT been uncertain before this
 * press — both callers do (`!wasUncertain`), and that pairing is what makes it
 * safe. For an attempt nothing had been sent under, a refusal, a missing
 * permission, a dead session or the rate limit is an answer with no charge
 * behind it. Once a press was sent, the server turns what follows its charge
 * into the maybe-taken `CONFLICT` (`phoneOutcome`, `savedOutcome`) — but not a
 * refusal that comes before the attempt is read (the session, the rate limit,
 * the booking permission), which is why the pairing is needed. Everything else
 * — no answer, a `CONFLICT`, a fault — may follow a charge.
 */
const REFUSED_BEFORE_CHARGING_CODES = new Set([
  'BAD_REQUEST',
  'FORBIDDEN',
  'UNAUTHORIZED',
  'TOO_MANY_REQUESTS',
])

export function refusedBeforeCharging(error: unknown): boolean {
  return (
    error instanceof TRPCClientError &&
    REFUSED_BEFORE_CHARGING_CODES.has(error.data?.code)
  )
}

/**
 * The server refused a referral code — every reason it refuses one arrives as
 * this one kind.
 *
 * `BAD_REQUEST` alone is not enough: the same previews answer it for input that
 * fails validation or a basket that cannot be priced, and taking an applied code
 * off for either charges the customer $25 more for nothing the code did.
 */
export function isReferralCodeRefusal(error: unknown): boolean {
  return (
    error instanceof TRPCClientError &&
    error.data?.refusalKind === REFERRAL_CODE_REFUSAL
  )
}

/**
 * No structured server error came back.
 *
 * **Named for what it can prove, which is less than you want it to be.** The
 * obvious name is "lost connection", and it is wrong: a fetch that never
 * completed lands here, but so does every answer that is not tRPC-shaped — a
 * gateway's HTML 502, a proxy's 504, any plain-text refusal from a middleware
 * mounted in front of the router. `httpBatchLink` calls `res.json()` on a
 * non-ok response without checking the content type, so all of them arrive as
 * a parse error with no `data`.
 *
 * Two things follow, and both have already been got wrong here:
 *
 * - **Never tell the user "nothing was sent."** A 504 means the API is *still
 *   executing* the request — that is why the proxy gave up — so the one case
 *   where the claim matters most is the case where it is false.
 * - **A refusal the server actually issued must not land here**, or retrying
 *   it hammers whatever refused. The catch-all rate limiter therefore returns
 *   a tRPC-shaped 429 (`TOO_MANY_REQUESTS_ERROR`, `apps/api/src/lib/
 *   rate-limit.ts`) rather than the library's default text body; without that
 *   envelope a 429 reads as "no answer" and gets retried into the limiter.
 */
export function isNoServerResponse(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data == null
}

/**
 * The request hit OUR OWN deadline (`fetchWithTimeout`) rather than failing.
 *
 * It is a strict subset of `isNoServerResponse` — an abort produces no
 * structured error, so that predicate is true too — and the distinction matters
 * in one place: **we should not immediately re-issue a request we ourselves
 * abandoned.** The server does not shed the work when the client gives up, so a
 * blanket retry adds load to something already too slow to answer, and on
 * `userPallet.extractInventoryFromPhotos` — the one model-backed procedure the
 * query default reaches, the others being mutations or raw client calls — it
 * bills the provider again for a call still running.
 *
 * The driver's per-mutation opt-in below is deliberately NOT gated on this:
 * each of those procedures either refuses the duplicate or absorbs it — see the
 * per-call-site justifications — and losing his confirmation is the worse
 * outcome.
 */
export function isClientTimeout(error: unknown): boolean {
  return (
    error instanceof TRPCClientError && error.cause?.name === 'TimeoutError'
  )
}

/**
 * The app's default query-retry rule, as a function rather than a closure
 * inside the `QueryClient`.
 *
 * It lives here so a surface that needs a STRICTER rule can compose with it
 * instead of restating it. A `retry` passed at a call site REPLACES this
 * wholesale — TanStack takes the per-query option and never merges — so a
 * hand-written `failureCount < 3` beside it is a silent regression on both
 * exclusions below, each of which was added for a measured failure. Delegating
 * means a rule added here reaches every stricter caller for free.
 *
 * Neither an UNAUTHORIZED nor a deadline WE imposed is worth repeating.
 * Retrying the first cannot change the answer; retrying the second multiplies
 * load on a server that is evidently already too slow to answer and has not
 * stopped working on the abandoned request — and on
 * `userPallet.extractInventoryFromPhotos`, whose own call site takes care to
 * fire one billed vision call per explicit tap, it would bill up to four.
 */
export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
): boolean {
  return (
    !isUnauthorized(error) &&
    !isClientTimeout(error) &&
    failureCount < QUERY_RETRIES
  )
}

/** How many times a retryable query is repeated before it is reported failed. */
export const QUERY_RETRIES = 3

/**
 * Retry predicate for a READ whose refusal is settled.
 *
 * A 4xx is an answer, not a failure — not yours, not found, not allowed — and
 * re-asking cannot change it, so a page waiting on the retries shows a
 * skeleton for the whole backoff before it can say what happened. A server
 * fault or a dropped connection IS worth one more try.
 *
 * **It DELEGATES to `shouldRetryQuery` and only narrows**, which is what that
 * function's own docblock asks of a stricter caller: a per-query `retry`
 * replaces the client-wide policy rather than composing with it, so a rule
 * added there would otherwise reach the default and not this. Restating it
 * here would put two conventions over one default in one file —
 * `retryInvoiceRead` already delegates — and the divergence would be silent.
 * The extra `isUnauthorized` term delegation brings is inert rather than a
 * behaviour change: a 401 carries `httpStatus` 401, which the settled-refusal
 * test below already rejects.
 */
export function retryOnlyUnsettledFailure(
  failureCount: number,
  error: unknown,
): boolean {
  if (settledRefusal(error)) return false
  return shouldRetryQuery(failureCount, error) && failureCount < 1
}

/**
 * A refusal the server has ANSWERED — any 4xx.
 *
 * Read off `httpStatus`, which `error-shape.ts` leaves exactly as tRPC built
 * it (only the message and stack are withheld), so it is present on every
 * structured error this can see.
 */
function settledRefusal(error: unknown): boolean {
  const status =
    error instanceof TRPCClientError ? error.data?.httpStatus : undefined
  return status !== undefined && status >= 400 && status < 500
}

/**
 * Retry predicate for a mutation a dropped signal must not lose.
 *
 * Retrying a mutation is **at-least-once delivery**, not exactly-once: a fetch
 * that rejects may still have reached the server and committed, with only the
 * response lost. So it is safe only where the server absorbs the duplicate or
 * refuses it — which is a property of each procedure, not of the transport.
 *
 * That is why this is opted into per mutation rather than set as a `mutations`
 * default on the client. A default would also cover the checkout and
 * overdue-payment paths, where a duplicate moves money, and append-only writes
 * like `driver.recordLabelPrint` → `labelPrintService.record`, which appends a
 * row per label and so would record a print that happened once as two.
 */
export function retryWhenNoServerResponse(
  failureCount: number,
  error: unknown,
): boolean {
  return isNoServerResponse(error) && failureCount < 2
}

/**
 * What a DRIVER reads when we could not reach the API at all.
 *
 * Compiled in rather than catalogued because the catalog
 * (`@stopal/shared/customer-messages`) is customer-scoped and Ops-editable —
 * a driver is staff, and their copy is not something Ops rewords. A customer
 * surface must pass the `pallet.failure.unreachable` slug instead.
 */
export const COULD_NOT_REACH =
  'Couldn’t reach Stopal — check your connection and try again.'

/**
 * What a failed call gets to say when the server's own words cannot be used.
 *
 * Two strings rather than one because the two cases are different claims, and
 * `client-network-state.md` is explicit that saying the wrong one is worse than
 * saying nothing: a gateway 504 means the API is still *executing* the request,
 * so "we couldn't reach the server" is honest while "nothing happened" is not.
 */
export interface FailureCopy {
  /** Nothing answered — a dead connection, a gateway, a proxy. */
  unreachable: string
  /** Something answered, but with nothing a person can read. */
  unexpected: string
}

/** The staff-facing pair. Customer surfaces pass catalog copy instead. */
export const DRIVER_FAILURE_COPY: FailureCopy = {
  unreachable: COULD_NOT_REACH,
  unexpected: 'Something went wrong at our end. Try that again in a moment.',
}

/**
 * The codes whose message was written by a person, as an ALLOWLIST.
 *
 * An allowlist because the alternative fails in the expensive direction: a
 * denylist of "codes whose text is raw" is a guess about every code we did not
 * think of, and being wrong means shipping an exception's text to a customer.
 * Being wrong the other way costs a vaguer sentence.
 *
 * Every entry corresponds to something `errorMapper` (`apps/api/src/trpc/
 * index.ts`) produces from a `DomainError`, plus the rate limiter's enveloped
 * 429. Those are the paths where a person wrote the words. **`UNAUTHORIZED` is
 * deliberately absent** — the auth middlewares supply `'Not authenticated'`,
 * which is a developer's sentence, and a signed-out customer is handled by the
 * app shell's redirect rather than by a callout.
 *
 * **`SERVICE_UNAVAILABLE` is absent for the opposite reason, and adding it "for
 * consistency" is a regression rather than a leak.** Its message IS written for
 * a person — `UpstreamError`, thrown by every AI transport failure — but it is
 * necessarily generic, because one sentence covers a provider 429, a timeout
 * and an unparseable answer alike. **On the estimator** the surface's own
 * catalog copy names the step instead ("we couldn't read those photos"; the
 * advisor's "we couldn't put together the packing advice, but your pallet count
 * is below"), which is what tells someone what to retry — and in the advisor's
 * case is the difference between keeping the number they came for and being
 * told the whole thing failed. Allowlisting the code replaces all of it.
 *
 * **The pallet-detail AI surfaces have no such per-step copy**, and this is the
 * honest cost of the decision rather than an argument against it: the in-app
 * advisor and the photo extractor both pass `pallet.failure.unexpected`
 * ("Something went wrong at our end"), which mildly misattributes an upstream
 * 429 to us. It is still a sentence written for a customer with usable advice
 * attached, and it is the same string every other pallet mutation shows, so the
 * fix if it ever matters is a slug of their own — not this allowlist, which
 * would degrade the estimator to buy it.
 *
 * **One array, exported, with the type and the Set both derived from it.**
 * `apps/web/e2e/trpc-stub.ts` builds stubbed refusals, and which code a stub
 * carries is what decides whether the surface shows the server's sentence at
 * all — so a hand-listed second copy over there would be this same allowlist
 * with none of its guarantees, and a code added here would not reach it.
 * Nothing else in `src` reads the array; the export exists for that consumer.
 */
export const AUTHORED_CODES = [
  'NOT_FOUND',
  'FORBIDDEN',
  'CONFLICT',
  'BAD_REQUEST',
  'TOO_MANY_REQUESTS',
] as const

/** A code whose message a person wrote — the values of {@link AUTHORED_CODES}. */
export type AuthoredCode = (typeof AUTHORED_CODES)[number]

const AUTHORED_CODE_SET: ReadonlySet<string> = new Set(AUTHORED_CODES)

/**
 * Is this server message a sentence written for the person reading it?
 *
 * **The premise of showing the server's message is that a human wrote it**, and
 * that premise is false more often than it looks, because the API mounts
 * `initTRPC.create()` with **no `errorFormatter`**. tRPC's own constructor is
 * `message ?? cause?.message ?? code`, so three things arrive looking exactly
 * like authored copy:
 *
 * - **An unhandled throw** — its `.message` is the raw exception. A null deref
 *   reaches the customer as `Cannot read properties of undefined (reading
 *   'id')`, and a constraint violation as `duplicate key value violates unique
 *   constraint "…"`, which additionally names our schema.
 * - **A `TRPCError` with no `message`** — renders as the literal `FORBIDDEN`.
 * - **A Zod input failure** — renders the stringified issues array, a JSON blob.
 *
 * The domain errors this app actually throws are unaffected: `errorMapper`
 * gives them a real code and their own sentence, which is the whole point.
 *
 * **A fourth shape rule goes in `isAuthoredMessageShape`, not here.** The three
 * above live there so the e2e harness enforces the same set when it builds a
 * stubbed refusal; adding one to this function's body instead would be enforced
 * in the app and invisible to the fixture, which is the split that predicate
 * exists to close.
 */
function isAuthoredSentence(error: unknown): error is Error {
  if (!(error instanceof TRPCClientError)) return false
  const code = error.data?.code
  if (typeof code !== 'string' || !AUTHORED_CODE_SET.has(code)) return false
  return isAuthoredMessageShape(error.message, code)
}

/**
 * The half of the question the CODE cannot answer: does this text read as
 * something a person wrote?
 *
 * Split out and exported so the e2e harness (`apps/web/e2e/trpc-stub.ts`) can
 * refuse to build a stubbed refusal this would reject — a fixture whose own
 * assertion could then never pass. Restating these three rules over there kept
 * them in sync by nothing, which is the same defect as a hand-copied allowlist:
 * a fourth rule added here would silently stop being enforced at the fixture.
 */
export function isAuthoredMessageShape(
  rawMessage: string,
  code: string,
): boolean {
  const message = rawMessage.trim()
  if (message.length === 0) return false
  // A `TRPCError` thrown with no message renders as its own code.
  if (message === code) return false
  // `BAD_REQUEST` is the one allowlisted code tRPC also uses for its own input
  // validation, where the message is `ZodError.message` — a JSON issue array
  // carrying our field paths and, on Zod 4, the caller's echoed input. The
  // check is not narrowed to that code: any allowlisted code can carry a blob.
  if (message.startsWith('[') || message.startsWith('{')) return false
  return true
}

/**
 * The sentence to show for a failed call.
 *
 * A server that answered has already written a sentence aimed at the person
 * reading it ("This stop is already closed"), so that is shown as itself —
 * **when it really was written for them**; see `isAuthoredSentence`, which is
 * the guard that keeps a stack-trace fragment off a customer's screen. A
 * failure with no answer behind it has only a transport string, and those are
 * not English: a driver was being shown `Failed to fetch`, and — for anything a
 * gateway answers on the API's behalf — the literal `Failed to parse JSON`.
 *
 * **`copy` is REQUIRED, deliberately.** It was a default parameter, and a
 * default is the wrong shape for this argument: there are two legitimate
 * sources — this module's constants for staff, and the Ops-editable catalog
 * slugs for customers — so whichever is the default becomes what you get by
 * FORGETTING, on the surface where it is wrong. A customer surface that omitted
 * it would silently ship copy Ops cannot edit, and nothing would fail:
 * `pallets-mutation-feedback.test.ts` asserts that a failure is surfaced, never
 * which sentence surfaces it.
 */
export function failureMessage(error: unknown, copy: FailureCopy): string {
  if (isNoServerResponse(error)) return copy.unreachable
  return isAuthoredSentence(error) ? error.message : copy.unexpected
}
